import { FormEvent, useEffect, useState } from "react";
import "../../amplifyClient";
import React from "react";
import { Button } from "@shared/ui";
import { Smartphone } from "lucide-react";
import { InlineError } from "../../components/Field";
import StepBody from "../../components/StepBody";
import StepFooter from "../../components/StepFooter";
import StepHeading from "../../components/StepHeading";
import { handleReturningUserSignIn } from "../../lib/authUtility";
import PhoneInputField, {
  isPhoneFieldError,
  PHONE_FIELD_ERRORS,
  validatePhoneNumber,
} from "../../components/PhoneInputField";

interface AxleAccountSignInProps {
  phoneNumber: string;
  setPhoneNumber: (phoneNumber: string) => void;
  step: string;
  nextStep: (nextStep: string, noHistory?: boolean) => void;
  setShowNav: (show: boolean) => void;
  posthog: any;
}

export default function AxleAccountSignIn({
  phoneNumber,
  setPhoneNumber,
  nextStep,
  setShowNav,
  posthog,
  step,
}: AxleAccountSignInProps) {
  const [error, setError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Effect for PostHog tracking
  useEffect(() => {
    posthog.capture("$pageview", { step });
  }, [posthog]);

  /* ---------------------------------------------------------------------- */
  /* Main submit handler                                                    */
  /* ---------------------------------------------------------------------- */
  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);
    /* -- basic validation ------------------------------------------------- */
    if (!phoneNumber) {
      setError(PHONE_FIELD_ERRORS.missing);
      setIsSubmitting(false);
      return;
    }
    if (!validatePhoneNumber(phoneNumber, "US")) {
      setError(PHONE_FIELD_ERRORS.invalid);
      setIsSubmitting(false);
      return;
    }

    // Hide the nav so Back can't race the sign-in
    setShowNav(false);
    await handleReturningUserSignIn(
      phoneNumber,
      nextStep,
      setError,
      (submitting: boolean) => {
        setIsSubmitting(submitting);
        setShowNav(!submitting);
      }
    );
  }

  /* ------------------------------  JSX  ---------------------------------- */
  return (
    <form
      onSubmit={handleSubmit}
      className="flex min-h-0 flex-1 flex-col phone-entry-screen"
    >
      <StepBody>
        <div className="flex flex-col gap-4">
          {/* Icon */}
          <div className="enter-phone-illustration max-h-10">
            <span className="flex size-10 items-center justify-center rounded border border-subtle bg-surface-muted">
              <Smartphone aria-hidden className="size-5" strokeWidth={1.75} />
            </span>
          </div>
          {/* Main content */}
          <div className="main-copy">
            <StepHeading
              title="Sign in with your phone"
              description="We’ll text you a code to find carriers you’ve already connected with Axle."
            />
          </div>
        </div>
        <div className="flex flex-col gap-4">
          {/* Error message */}
          {error && !isPhoneFieldError(error) && (
            <div className="rounded border border-danger-border p-3">
              <InlineError>{error}</InlineError>
            </div>
          )}

          {/* Phone input */}
          <PhoneInputField
            value={phoneNumber}
            onChange={setPhoneNumber}
            country="US"
            onCountryChange={() => {}}
            showCountryFlags={true}
            disabled={isSubmitting}
            error={isPhoneFieldError(error) ? error : undefined}
          />
        </div>
      </StepBody>
      {/* Action buttons */}
      <StepFooter>
        <Button
          type="submit"
          onClick={() => {
            posthog.capture("axle_account_sign_in_continue");
          }}
          name="continue"
        >
          {isSubmitting ? "Submitting..." : "Continue"}
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            posthog.capture("axle_account_sign_in_continue_as_guest");
            nextStep("permissions");
          }}
          name="guest"
          type="button"
        >
          Continue as guest
        </Button>
      </StepFooter>
    </form>
  );
}
